import React, { useState } from "react";
import { Container, Paper, Typography, Box, Grid, Chip, Stack, Button, TextField, Alert, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import { Psychology, Business, Person, Code, Assignment, TrendingUp, EmojiEvents, Notes, PictureAsPdf, Close, Download, Visibility } from "@mui/icons-material";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const AIAnalysisReport = () => {
  const [jsonData, setJsonData] = useState("");
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showAllTasks, setShowAllTasks] = useState(false);

  // Sample data for testing (based on actual n8n output format)
  const sampleData = [
    {
      "student_id": "65050249",
      "student_name": "นางสาวอัญชุดี ตันเจริญ",
      "internship": {
        "company": "บริษัท อนาล็อก ดีไวด์เซล (ประเทศไทย) จำกัด",
        "role": "Test Product Engineer",
        "duration": {
          "start_date": "2025-05-13",
          "end_date": "2025-05-30"
        }
      },
      "tech_stack": [
        "AI", "Object detection", "Dataset collection", "C#", "Git/GitHub",
        "op-amp", "BJT", "Confusion matrix", "Automation machine", "Motor",
        "Image augmentation", "Video capture programming"
      ],
      "tasks": [
        {
          "summary": "ฟังชี้แจงรายละเอียดของงานและข้อบังคับต่าง ๆ (orientation)",
          "evidence_word": []
        },
        {
          "summary": "Assignment 1: เทรน AI สำหรับตรวจจับวัตถุร้อน (ใช้ชุดภาพความร้อน)",
          "evidence_word": []
        },
        {
          "summary": "Assignment 2: สร้างโปรแกรมบันทึกภาพจากวิดีโอ และเรียนภาษา C#",
          "evidence_word": []
        }
      ],
      "skills": [
        {
          "name": "การเขียนโค้ด (programming)",
          "category": "programming",
          "level": "beginner",
          "evidence_word": ""
        },
        {
          "name": "AI",
          "category": "programming",
          "level": "intermediate",
          "evidence_word": ""
        },
        {
          "name": "การทำงานเป็นทีม (teamwork)",
          "category": "soft_skills",
          "level": "good",
          "evidence_word": ""
        }
      ],
      "soft_skills": [
        {
          "name": "การทำงานเป็นทีม (teamwork)",
          "level": "good",
          "evidence_word": ""
        },
        {
          "name": "การประชุมและประสานงาน",
          "level": "developing",
          "evidence_word": ""
        }
      ],
      "outcomes": [
        {
          "type": "deliverable",
          "detail": "Assignment 1: model สำหรับตรวจจับวัตถุร้อน",
          "evidence_word": []
        },
        {
          "type": "deliverable", 
          "detail": "Assignment 2: โปรแกรมบันทึกภาพจากวิดีโอ",
          "evidence_word": []
        }
      ],
      "notes": "ปัญหาและอุปสรรค: การปรับตัวกับระบบของบริษัทในช่วงแรก และอุปกรณ์ในการทำงานมีความล่าช้า",
      "ai_analysis": {
        "summary": "นิสิตแสดงความก้าวหน้าดีในด้าน AI และ programming โดยเฉพาะงาน object detection และ C# programming มีการเรียนรู้ทักษะใหม่ๆ อย่างต่อเนื่อง รวมถึงการทำงานเป็นทีมและการนำเสนอผลงาน",
        "performance_score": 85,
        "strengths": [
          "เรียนรู้เทคโนโลยี AI ได้เร็วและมีประสิทธิภาพ",
          "ปรับตัวกับภาษา C# และเครื่องมือใหม่ได้ดี", 
          "มีการทำงานเป็นทีมและประสานงานที่ดี",
          "สามารถจัดทำเอกสารและนำเสนอได้"
        ],
        "improvements": [
          "ควรพัฒนาความเข้าใจในระบบบริษัทให้มากขึ้น",
          "การจัดการเวลาและทรัพยากรต้องปรับปรุง",
          "ทักษะด้าน hardware ยังต้องเสริมสร้าง"
        ],
        "recommendations": [
          "ควรได้รับการฝึกอบรมเพิ่มเติมในด้าน electronics และ hardware",
          "แนะนำให้ทำโปรเจคที่ผสานทั้ง AI และ embedded systems",
          "ส่งเสริมให้เป็นหัวหน้าทีมในโปรเจคขนาดเล็ก"
        ]
      }
    }
  ];

  const parseJsonData = () => {
    setError("");
    setReportData(null);

    if (!jsonData.trim()) {
      setError("❌ กรุณาใส่ข้อมูล JSON");
      return;
    }

    try {
      let parsed;
      
      // ลองแปลง JSON ก่อน
      try {
        parsed = JSON.parse(jsonData);
      } catch (e) {
        // ถ้าแปลงไม่ได้ อาจจะเป็นข้อมูลไม่สมบูรณ์ ลองแก้ไข
        console.log("🔧 พยายามแก้ไข JSON...");
        
        let fixedJson = jsonData;
        
        // แก้ไขปัญหา JSON ที่พบบ่อย
        if (!fixedJson.startsWith('[') && !fixedJson.startsWith('{')) {
          fixedJson = '[' + fixedJson + ']';
        }
        if (!fixedJson.endsWith(']') && !fixedJson.endsWith('}')) {
          if (fixedJson.startsWith('[')) {
            fixedJson = fixedJson + ']';
          } else {
            fixedJson = fixedJson + '}';
          }
        }
        
        // ลบ comma ที่เหลือ
        fixedJson = fixedJson.replace(/,\s*}/, '}').replace(/,\s*]/, ']');
        
        parsed = JSON.parse(fixedJson);
      }

      // รองรับทั้งแบบ array และ object
      if (Array.isArray(parsed)) {
        // กรณี n8n ส่งมาเป็น array
        if (parsed.length > 0) {
          setReportData(parsed[0]); // เอาตัวแรก
          console.log("✅ JSON Array ถูกต้อง:", parsed[0]);
        } else {
          setError("❌ Array ว่างเปล่า");
          return;
        }
      } else {
        // กรณีเป็น object เดียว
        setReportData(parsed);
        console.log("✅ JSON Object ถูกต้อง:", parsed);
      }

    } catch (error) {
      console.error("❌ JSON Error:", error);
      setError(`❌ JSON format ไม่ถูกต้อง: ${error.message}\n\n💡 กรุณาตรวจสอบ:\n- ครบ [ ] หรือ { } หรือไม่\n- ใช้ " สำหรับ key และ value\n- ไม่มี , เกิน\n- รูปแบบ JSON ถูกต้อง`);
    }
  };

  const loadSampleData = () => {
    setJsonData(JSON.stringify(sampleData, null, 2));
    setReportData(sampleData[0]); // เอาตัวแรกจาก array
    setError("");
    console.log("🎯 โหลดข้อมูลตัวอย่างแล้ว");
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "success";
    if (score >= 60) return "warning";
    return "error";
  };

  const openPreview = () => {
    if (!reportData) {
      setError("❌ กรุณาวิเคราะห์ข้อมูลก่อนพรีวิว PDF");
      return;
    }
    setPreviewOpen(true);
    // สร้าง PDF สำหรับพรีวิว
    setTimeout(() => {
      generateSkillMappingPDF(false);
    }, 300);
  };

  const generateSkillMappingPDF = (downloadNow = false) => {
    if (!reportData) {
      setError("❌ กรุณาวิเคราะห์ข้อมูลก่อนสร้าง PDF");
      return;
    }

    try {
      setError(""); // Clear previous errors
      
      // สร้าง HTML content สำหรับ PDF
      const pdfContent = document.createElement('div');
      pdfContent.style.fontFamily = '"Sarabun", "Kanit", "Noto Sans Thai", Arial, sans-serif';
      pdfContent.style.padding = '20px';
      pdfContent.style.backgroundColor = 'white';
      pdfContent.style.fontSize = '14px';
      pdfContent.style.lineHeight = '1.6';
      pdfContent.style.width = '800px';
      pdfContent.style.minHeight = '1000px';
      pdfContent.style.position = 'absolute';
      pdfContent.style.left = '-9999px';
      pdfContent.style.top = '0';

      // เพิ่ม Google Fonts
      const fontLink = document.createElement('link');
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700&family=Kanit:wght@300;400;500;600;700&display=swap';
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);

      // กำหนด CSS สำหรับตาราง
      const styles = `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;500;600;700&family=Kanit:wght@300;400;500;600;700&display=swap');
        
        .pdf-container {
          font-family: 'Sarabun', 'Kanit', 'Noto Sans Thai', Arial, sans-serif;
          padding: 20px;
          background: white;
          font-size: 14px;
          line-height: 1.6;
          max-width: 800px;
          color: #000;
          -webkit-font-feature-settings: "liga", "kern";
          font-feature-settings: "liga", "kern";
          text-rendering: optimizeLegibility;
        }
        .header {
          text-align: center;
          font-size: 20px;
          font-weight: 600;
          margin-bottom: 30px;
          font-family: 'Kanit', sans-serif;
        }
        .info-section {
          margin-bottom: 20px;
        }
        .info-row {
          margin-bottom: 8px;
        }
        .label {
          font-weight: 600;
          display: inline-block;
          width: 140px;
          font-family: 'Kanit', sans-serif;
        }
        .value {
          font-family: 'Sarabun', sans-serif;
        }
        .skills-table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          font-family: 'Sarabun', sans-serif;
        }
        .skills-table th,
        .skills-table td {
          border: 1px solid #ccc;
          padding: 8px;
          text-align: left;
          vertical-align: top;
        }
        .skills-table th {
          background-color: #f0f0f0;
          font-weight: 600;
          font-family: 'Kanit', sans-serif;
        }
        .skills-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .section-title {
          font-size: 16px;
          font-weight: 600;
          margin: 20px 0 10px 0;
          font-family: 'Kanit', sans-serif;
          border-bottom: 2px solid #1976d2;
          padding-bottom: 5px;
        }
        .summary {
          margin: 20px 0;
          text-align: justify;
          font-family: 'Sarabun', sans-serif;
          line-height: 1.8;
        }
        .tech-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin: 10px 0;
        }
        .tech-tag {
          background: #e3f2fd;
          border: 1px solid #1976d2;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-family: 'Sarabun', sans-serif;
        }
        .outcome-item {
          margin: 8px 0;
          padding: 8px;
          border-left: 3px solid #4caf50;
          background: #f1f8f4;
        }
        .outcome-type {
          display: inline-block;
          padding: 2px 8px;
          background: #4caf50;
          color: white;
          border-radius: 8px;
          font-size: 11px;
          margin-right: 8px;
          font-family: 'Kanit', sans-serif;
        }
        .task-item {
          margin: 6px 0;
          padding: 6px 10px;
          background: #fafafa;
          border-left: 2px solid #ff9800;
          font-size: 13px;
        }
        .footer {
          margin-top: 30px;
          page-break-before: avoid;
        }
      </style>
      `;

      // สร้าง HTML content
      pdfContent.innerHTML = `
        ${styles}
        <div class="pdf-container">
          <div class="header">Skill Mapping Certificate (Internship)</div>
          
          <div class="info-section">
            <div class="info-row">
              <span class="label">Name:</span> 
              <span class="value">${sanitizeText(reportData.student_name) || 'N/A'}</span>
            </div>
            <div class="info-row">
              <span class="label">Student ID:</span> 
              <span class="value">${reportData.student_id || 'N/A'}</span>
            </div>
            <div class="info-row">
              <span class="label">Program:</span> 
              <span class="value">Embedded Systems & Communication Electronics, Faculty of Engineering</span>
            </div>
            <div class="info-row">
              <span class="label">Organization:</span> 
              <span class="value">${sanitizeText(reportData.internship?.company) || 'N/A'}</span>
            </div>
            <div class="info-row">
              <span class="label">Position:</span> 
              <span class="value">${reportData.internship?.role || 'N/A'}</span>
            </div>
            <div class="info-row">
              <span class="label">Period:</span> 
              <span class="value">${reportData.internship?.duration ? 
                `${reportData.internship.duration.start_date} - ${reportData.internship.duration.end_date}` : 
                'N/A'}</span>
            </div>
          </div>

          ${generateTechStackSection()}
          
          <div class="section-title">Skills Assessment</div>
          
          <table class="skills-table">
            <thead>
              <tr>
                <th style="width: 30%">Skill Area</th>
                <th style="width: 15%">Level (0-5)</th>
                <th style="width: 55%">Evidence (Activities)</th>
              </tr>
            </thead>
            <tbody>
              ${generateSkillRows()}
            </tbody>
          </table>

          ${generateTasksSection(showAllTasks)}
          
          ${generateOutcomesSection()}

          <div class="section-title">Summary</div>
          <div class="summary">
            ${generateSafeSummary()}
          </div>

          <div class="footer">
            <div class="info-row">
              <span class="label">Issued by:</span> 
              <span class="value">Faculty of Engineering, Burapha University</span>
            </div>
            <div class="info-row">
              <span class="label">Date:</span> 
              <span class="value">${new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
          </div>
        </div>
      `;

      // เพิ่ม element ลงใน body ชั่วคราว
      document.body.appendChild(pdfContent);

      // รอให้ font โหลดเสร็จก่อนสร้าง PDF
      setTimeout(() => {
        const targetElement = pdfContent.querySelector('.pdf-container');
        
        if (!targetElement) {
          setError("❌ ไม่สามารถสร้าง PDF content ได้");
          if (document.body.contains(pdfContent)) {
            document.body.removeChild(pdfContent);
          }
          if (document.head.contains(fontLink)) {
            document.head.removeChild(fontLink);
          }
          return;
        }

        // Log เพื่อ debug
        console.log("📄 Target element:", targetElement);
        console.log("📏 Element height:", targetElement.scrollHeight);

        html2canvas(targetElement, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: true,
          width: 800,
          backgroundColor: '#ffffff',
          windowWidth: 800,
          windowHeight: targetElement.scrollHeight,
          onclone: (clonedDoc) => {
            const clonedElement = clonedDoc.querySelector('.pdf-container');
            if (clonedElement) {
              clonedElement.style.display = 'block';
              clonedElement.style.visibility = 'visible';
              clonedElement.style.opacity = '1';
            }
          }
        }).then(canvas => {
        console.log("✅ Canvas created:", canvas.width, "x", canvas.height);
        
        const imgData = canvas.toDataURL('image/png', 1.0);
        const pdf = new jsPDF('p', 'mm', 'a4');
        
        const imgWidth = 210; // A4 width in mm
        const pageHeight = 295; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        
        let position = 0;

        // เพิ่มภาพลงใน PDF
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        // ถ้าเนื้อหายาวเกิน 1 หน้า
        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        // บันทึก PDF
        const fileName = `Skill_Mapping_Certificate_${reportData.student_id || 'Unknown'}_${(reportData.student_name || 'Student').replace(/\s+/g, '_')}.pdf`;
        
        if (downloadNow) {
          pdf.save(fileName);
          console.log("✅ PDF ดาวน์โหลดเรียบร้อยแล้ว:", fileName);
          setPreviewOpen(false);
        } else {
          // สร้าง PDF blob สำหรับพรีวิว
          const pdfBlob = pdf.output('blob');
          const pdfUrl = URL.createObjectURL(pdfBlob);
          
          // เปิด preview ใน iframe
          const previewFrame = document.getElementById('pdf-preview-frame');
          if (previewFrame) {
            previewFrame.src = pdfUrl;
          }
          
          console.log("✅ PDF พรีวิวพร้อม");
        }
        
        // ลบ element ออกจาก body
        if (document.body.contains(pdfContent)) {
          document.body.removeChild(pdfContent);
        }
        if (document.head.contains(fontLink)) {
          document.head.removeChild(fontLink);
        }
        }).catch(error => {
          console.error("❌ Error creating PDF:", error);
          setError(`❌ เกิดข้อผิดพลาดในการสร้าง PDF: ${error.message || 'Unknown error'}`);
          if (document.body.contains(pdfContent)) {
            document.body.removeChild(pdfContent);
          }
          if (document.head.contains(fontLink)) {
            document.head.removeChild(fontLink);
          }
        });
      }, 2000); // รอ 2 วินาที ให้ font และ content โหลดเสร็จ
    } catch (error) {
      console.error("❌ Unexpected error:", error);
      setError(`❌ เกิดข้อผิดพลาดไม่คาดคิด: ${error.message || 'Unknown error'}`);
    }
  };

  // ฟังก์ชันสร้างส่วน Tech Stack
  const generateTechStackSection = () => {
    if (!reportData.tech_stack || reportData.tech_stack.length === 0) return '';
    
    return `
      <div class="section-title">Technologies & Tools Used</div>
      <div class="tech-tags">
        ${reportData.tech_stack.map(tech => `<span class="tech-tag">${tech}</span>`).join('')}
      </div>
    `;
  };

  // ฟังก์ชันสร้างส่วน Tasks
  const generateTasksSection = (showAll = false) => {
    if (!reportData.tasks || reportData.tasks.length === 0) return '';
    
    const tasksToShow = showAll ? reportData.tasks : reportData.tasks.slice(0, 10);
    
    return `
      <div class="section-title">Major Tasks Completed (${reportData.tasks.length} tasks)</div>
      ${tasksToShow.map((task, index) => `
        <div class="task-item">
          <strong>Task ${index + 1}:</strong> ${task.summary}
        </div>
      `).join('')}
      ${!showAll && reportData.tasks.length > 10 ? `<div style="text-align: center; margin-top: 10px; color: #666; font-size: 12px;">... and ${reportData.tasks.length - 10} more tasks</div>` : ''}
    `;
  };

  // ฟังก์ชันสร้างส่วน Outcomes
  const generateOutcomesSection = () => {
    if (!reportData.outcomes || reportData.outcomes.length === 0) return '';
    
    return `
      <div class="section-title">Project Outcomes & Deliverables</div>
      ${reportData.outcomes.map(outcome => `
        <div class="outcome-item">
          <span class="outcome-type">${outcome.type}</span>
          ${outcome.detail}
        </div>
      `).join('')}
    `;
  };

  // ฟังก์ชันสร้างแถวตาราง skills แบบ dynamic จริงๆ จาก n8n data
  const generateSkillRows = () => {
    const skillRows = [];

    // 1. สร้าง Skills จาก skills array ใน n8n โดยตรง
    if (reportData.skills && reportData.skills.length > 0) {
      // จัดกลุ่ม skills ตาม category
      const skillsByCategory = {};
      
      reportData.skills.forEach(skill => {
        const category = skill.category || 'General';
        if (!skillsByCategory[category]) {
          skillsByCategory[category] = [];
        }
        skillsByCategory[category].push(skill);
      });

      // สร้างแถวสำหรับแต่ละ category
      Object.keys(skillsByCategory).forEach(category => {
        const skills = skillsByCategory[category];
        const level = calculateAverageLevel(skills);
        
        // สร้าง evidence จาก skill names และ tech_stack ที่เกี่ยวข้อง
        const skillNames = skills.map(s => s.name).join(', ');
        const relatedTech = reportData.tech_stack?.filter(tech => 
          skills.some(skill => 
            tech.toLowerCase().includes(skill.name.toLowerCase()) ||
            skill.name.toLowerCase().includes(tech.toLowerCase()) ||
            (category === 'programming' && (tech.includes('C#') || tech.includes('Git') || tech.includes('Python'))) ||
            (category === 'hardware' && (tech.includes('op-amp') || tech.includes('BJT') || tech.includes('Motor')))
          )
        ) || [];
        
        const evidence = [...new Set([...relatedTech, skillNames])].join(', ');
        
        // แปลงชื่อ category ให้เหมาะสม
        let categoryName = category;
        switch(category.toLowerCase()) {
          case 'programming': categoryName = 'Programming/Development'; break;
          case 'hardware': categoryName = 'Electronics/Hardware'; break;
          case 'soft_skills': categoryName = 'Soft Skills'; break;
          case 'tools': categoryName = 'Tools & Platforms'; break;
          default: categoryName = category.charAt(0).toUpperCase() + category.slice(1);
        }
        
        skillRows.push([categoryName, level, evidence]);
      });
    }

    // 2. สร้าง Skills จาก soft_skills array แยกต่างหาก
    if (reportData.soft_skills && reportData.soft_skills.length > 0) {
      const level = calculateAverageLevel(reportData.soft_skills);
      const evidence = reportData.soft_skills.map(skill => skill.name).join(', ');
      
      // ตรวจว่ามี Soft Skills จาก skills array แล้วหรือยัง
      const hasSoftSkills = skillRows.some(row => row[0].includes('Soft'));
      if (!hasSoftSkills) {
        skillRows.push(['Soft Skills', level, evidence]);
      }
    }

    // 3. สร้าง Skills จาก tech_stack ที่ไม่ได้อยู่ใน skills array
    if (reportData.tech_stack && reportData.tech_stack.length > 0) {
      // จัดกลุ่ม tech_stack
      const aiTech = reportData.tech_stack.filter(tech => 
        tech.toLowerCase().includes('ai') || 
        tech.toLowerCase().includes('ml') ||
        tech.toLowerCase().includes('yolo') ||
        tech.toLowerCase().includes('machine learning')
      );
      
      const visionTech = reportData.tech_stack.filter(tech => 
        tech.toLowerCase().includes('detection') ||
        tech.toLowerCase().includes('dataset') ||
        tech.toLowerCase().includes('vision') ||
        tech.toLowerCase().includes('image') ||
        tech.toLowerCase().includes('video')
      );
      
      const progTech = reportData.tech_stack.filter(tech => 
        tech.includes('C#') ||
        tech.includes('Python') ||
        tech.includes('JavaScript') ||
        tech.includes('Git')
      );

      const hardwareTech = reportData.tech_stack.filter(tech => 
        tech.includes('op-amp') ||
        tech.includes('BJT') ||
        tech.includes('Motor') ||
        tech.toLowerCase().includes('automation')
      );

      // เพิ่มกลุ่มที่ยังไม่มี
      if (aiTech.length > 0 && !skillRows.some(row => row[0].includes('AI'))) {
        skillRows.push(['AI/Machine Learning', '4.0', aiTech.join(', ')]);
      }
      
      if (visionTech.length > 0 && !skillRows.some(row => row[0].includes('Vision'))) {
        skillRows.push(['Computer Vision', '3.5', visionTech.join(', ')]);
      }
      
      if (progTech.length > 0 && !skillRows.some(row => row[0].includes('Programming'))) {
        skillRows.push(['Programming Languages & Tools', '3.0', progTech.join(', ')]);
      }
      
      if (hardwareTech.length > 0 && !skillRows.some(row => row[0].includes('Electronic') || row[0].includes('Hardware'))) {
        skillRows.push(['Electronics & Hardware', '3.0', hardwareTech.join(', ')]);
      }
    }

    // 4. เพิ่ม Skills จาก tasks และ outcomes
    if (reportData.tasks && reportData.tasks.length > 0) {
      const documentationTasks = reportData.tasks.filter(task => 
        task.summary.toLowerCase().includes('paper') ||
        task.summary.toLowerCase().includes('เอกสาร') ||
        task.summary.toLowerCase().includes('รายงาน') ||
        task.summary.toLowerCase().includes('presentation') ||
        task.summary.toLowerCase().includes('chart')
      );

      const projectTasks = reportData.tasks.filter(task => 
        task.summary.toLowerCase().includes('project') ||
        task.summary.toLowerCase().includes('วางแผน') ||
        task.summary.toLowerCase().includes('gantt') ||
        task.summary.toLowerCase().includes('planning')
      );

      if (documentationTasks.length > 0 && !skillRows.some(row => row[0].includes('Documentation'))) {
        const evidence = documentationTasks.map(task => 
          task.summary.substring(0, 50) + (task.summary.length > 50 ? '...' : '')
        ).join('; ');
        skillRows.push(['Technical Documentation', '3.5', evidence]);
      }

      if (projectTasks.length > 0 && !skillRows.some(row => row[0].includes('Management') || row[0].includes('Planning'))) {
        const evidence = projectTasks.map(task => 
          task.summary.substring(0, 50) + (task.summary.length > 50 ? '...' : '')
        ).join('; ');
        skillRows.push(['Project Management', '2.8', evidence]);
      }
    }

    // 5. ถ้ายังไม่มีข้อมูลเลย ให้ใส่จาก outcomes
    if (skillRows.length === 0 && reportData.outcomes && reportData.outcomes.length > 0) {
      const outcomesByType = {};
      reportData.outcomes.forEach(outcome => {
        const type = outcome.type || 'General';
        if (!outcomesByType[type]) {
          outcomesByType[type] = [];
        }
        outcomesByType[type].push(outcome);
      });

      Object.keys(outcomesByType).forEach(type => {
        const outcomes = outcomesByType[type];
        const evidence = outcomes.map(o => 
          o.detail.substring(0, 60) + (o.detail.length > 60 ? '...' : '')
        ).join('; ');
        
        let skillName = type.charAt(0).toUpperCase() + type.slice(1);
        switch(type.toLowerCase()) {
          case 'deliverable': skillName = 'Project Deliverables'; break;
          case 'report': skillName = 'Documentation & Reporting'; break;
          case 'bugfix': skillName = 'Problem Solving'; break;
          case 'feature': skillName = 'Feature Development'; break;
        }
        
        skillRows.push([skillName, '3.2', evidence]);
      });
    }

    // ถ้าไม่มีข้อมูลเลย ให้ใส่ค่า default
    if (skillRows.length === 0) {
      skillRows.push(['General Skills', '3.0', 'Internship activities and learning']);
    }

    return skillRows.map(([area, level, evidence]) => `
      <tr>
        <td>${area}</td>
        <td>${level}</td>
        <td>${evidence}</td>
      </tr>
    `).join('');
  };

  // ฟังก์ชันแปลงข้อความไทยเป็น English ถ้าจำเป็น
  const sanitizeText = (text) => {
    if (!text) return 'N/A';
    
    // แทนที่ข้อความไทยที่มักจะมีปัญหาด้วย English equivalent
    const replacements = {
      'นางสาว': 'Miss',
      'นาย': 'Mr.',
      'นาง': 'Mrs.',
      'บริษัท': 'Company',
      'จำกัด': 'Ltd.',
      'มหาชน': 'Public Co.',
      'ประเทศไทย': 'Thailand',
      'กรุงเทพฯ': 'Bangkok',
      'กรุงเทพมหานคร': 'Bangkok',
      'คณะวิศวกรรมศาสตร์': 'Faculty of Engineering',
      'มหาวิทยาลัยบูรพา': 'Burapha University'
    };
    
    let sanitized = text;
    Object.keys(replacements).forEach(thai => {
      sanitized = sanitized.replace(new RegExp(thai, 'g'), replacements[thai]);
    });
    
    return sanitized;
  };

  // ฟังก์ชันสำหรับสร้าง Summary ที่รองรับทั้งไทยและอังกฤษ
  const generateSafeSummary = () => {
    if (reportData.ai_analysis?.summary) {
      // ถ้ามี summary จาก AI analysis ให้ใช้
      return sanitizeText(reportData.ai_analysis.summary);
    }
    
    // สร้าง summary อัตโนมัติจากข้อมูลที่มีจริง แบบละเอียดและครบถ้วน
    const summaryParts = [];
    
    // 1. แนะนำข้อมูลพื้นฐาน
    const studentName = reportData.student_name ? sanitizeText(reportData.student_name.split(' ').slice(-2).join(' ')) : 'The student';
    const company = reportData.internship?.company ? sanitizeText(reportData.internship.company) : 'the organization';
    const position = reportData.internship?.role || 'intern position';
    
    summaryParts.push(`${studentName} completed an internship at ${company} as ${position}.`);
    
    // 2. วิเคราะห์ทักษะที่พัฒนา
    const skillAreas = [];
    const techHighlights = [];
    
    if (reportData.skills && reportData.skills.length > 0) {
      const categories = [...new Set(reportData.skills.map(s => s.category))];
      
      if (categories.includes('programming')) {
        const progSkills = reportData.skills.filter(s => s.category === 'programming');
        const aiRelated = progSkills.find(s => s.name.toLowerCase().includes('ai') || s.name.toLowerCase().includes('ml'));
        if (aiRelated) {
          skillAreas.push('AI/Machine Learning');
          techHighlights.push('developed AI models');
        } else {
          skillAreas.push('software development');
        }
      }
      
      if (categories.includes('hardware')) {
        skillAreas.push('electronics and hardware');
        techHighlights.push('worked with hardware components');
      }
      
      if (categories.includes('tools')) {
        skillAreas.push('development tools and platforms');
      }
    }
    
    // เพิ่มจาก tech_stack
    if (reportData.tech_stack && reportData.tech_stack.length > 0) {
      const hasAI = reportData.tech_stack.some(tech => 
        tech.toLowerCase().includes('ai') || 
        tech.toLowerCase().includes('yolo') ||
        tech.toLowerCase().includes('detection') ||
        tech.toLowerCase().includes('ml')
      );
      if (hasAI && !skillAreas.includes('AI/Machine Learning')) {
        skillAreas.push('AI/Machine Learning');
        techHighlights.push('implemented computer vision solutions');
      }
      
      const progLangs = reportData.tech_stack.filter(tech => 
        tech.includes('C#') || tech.includes('Python') || tech.includes('JavaScript') || tech.includes('Java')
      );
      if (progLangs.length > 0 && !skillAreas.some(s => s.includes('development'))) {
        skillAreas.push(`programming (${progLangs.slice(0, 2).join(', ')})`);
      }
      
      const hasGit = reportData.tech_stack.some(tech => tech.toLowerCase().includes('git'));
      if (hasGit && !techHighlights.includes('version control')) {
        techHighlights.push('utilized version control systems');
      }
    }
    
    // สร้างประโยคเกี่ยวกับทักษะ
    if (skillAreas.length > 0) {
      summaryParts.push(`The internship focused on ${skillAreas.slice(0, 3).join(', ')}, where the student demonstrated strong technical progress.`);
    }
    
    // 3. งานที่ทำและผลลัพธ์
    if (reportData.tasks && reportData.tasks.length > 0) {
      const taskCount = reportData.tasks.length;
      summaryParts.push(`Throughout the ${taskCount}-task program, key responsibilities included ${techHighlights.slice(0, 2).join(' and ') || 'various technical assignments'}.`);
    }
    
    // 4. ผลงานที่ส่งมอบ
    if (reportData.outcomes && reportData.outcomes.length > 0) {
      const deliverables = reportData.outcomes.filter(o => o.type === 'deliverable');
      const reports = reportData.outcomes.filter(o => o.type === 'report');
      const bugfixes = reportData.outcomes.filter(o => o.type === 'bugfix');
      const features = reportData.outcomes.filter(o => o.type === 'feature');
      
      const outcomeDesc = [];
      if (deliverables.length > 0) outcomeDesc.push(`${deliverables.length} deliverable${deliverables.length > 1 ? 's' : ''}`);
      if (reports.length > 0) outcomeDesc.push(`${reports.length} report${reports.length > 1 ? 's' : ''}`);
      if (features.length > 0) outcomeDesc.push(`${features.length} feature${features.length > 1 ? 's' : ''}`);
      if (bugfixes.length > 0) outcomeDesc.push(`${bugfixes.length} bug fix${bugfixes.length > 1 ? 'es' : ''}`);
      
      if (outcomeDesc.length > 0) {
        summaryParts.push(`Successfully completed ${outcomeDesc.join(', ')}.`);
      }
    }
    
    // 5. Soft Skills
    if (reportData.soft_skills && reportData.soft_skills.length > 0) {
      const softSkillNames = reportData.soft_skills.map(s => s.name.toLowerCase());
      const hasTeamwork = softSkillNames.some(s => s.includes('team') || s.includes('ทีม'));
      const hasCommunication = softSkillNames.some(s => s.includes('communication') || s.includes('ประสานงาน') || s.includes('ประชุม'));
      
      const softSkillDesc = [];
      if (hasTeamwork) softSkillDesc.push('effective teamwork');
      if (hasCommunication) softSkillDesc.push('clear communication');
      
      if (softSkillDesc.length > 0) {
        summaryParts.push(`Demonstrated ${softSkillDesc.join(' and ')} throughout the internship period.`);
      } else {
        summaryParts.push('Adapted well to the professional work environment and collaborated effectively with team members.');
      }
    }
    
    // 6. สรุปท้าย
    if (reportData.ai_analysis?.performance_score) {
      const score = reportData.ai_analysis.performance_score;
      if (score >= 80) {
        summaryParts.push('Overall performance was excellent, showing strong potential for future technical roles.');
      } else if (score >= 60) {
        summaryParts.push('Overall performance was satisfactory with areas identified for continued growth.');
      }
    } else {
      summaryParts.push('The experience provided valuable hands-on learning and professional development opportunities.');
    }
    
    return summaryParts.join(' ');
  };

  // ฟังก์ชันคำนวณ level เฉลี่ย
  const calculateAverageLevel = (skills) => {
    if (!skills || skills.length === 0) return '3.0';
    
    const levels = skills.map(skill => {
      switch(skill.level) {
        case 'excellent': case 'ดีมาก': return 4.5;
        case 'good': case 'ดี': return 4.0;
        case 'intermediate': case 'ปานกลาง': return 3.5;
        case 'developing': case 'กำลังพัฒนา': return 3.0;
        case 'beginner': case 'เริ่มต้น': return 2.5;
        default: 
          if (skill.level && !isNaN(skill.level)) return parseFloat(skill.level);
          return 3.0;
      }
    });
    
    const avg = levels.reduce((sum, level) => sum + level, 0) / levels.length;
    return avg.toFixed(1);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Psychology color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            📊 รายงานการวิเคราะห์ AI นิสิตฝึกงาน
          </Typography>
        </Box>

        {/* JSON Input Section */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: '#f5f5f5' }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Code /> นำเข้าข้อมูล JSON
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={10}
            value={jsonData}
            onChange={(e) => setJsonData(e.target.value)}
            placeholder="วางข้อมูล JSON จาก n8n ที่นี่..."
            variant="outlined"
            sx={{ mb: 2 }}
          />
          
          <Stack direction="row" spacing={2}>
            <Button 
              variant="contained" 
              onClick={parseJsonData}
              startIcon={<Psychology />}
            >
              🔍 วิเคราะห์ข้อมูล
            </Button>
            <Button 
              variant="outlined" 
              onClick={loadSampleData}
              startIcon={<Assignment />}
            >
              📋 โหลดข้อมูลตัวอย่าง
            </Button>
            {reportData && (
              <Button 
                variant="contained" 
                color="primary"
                onClick={openPreview}
                startIcon={<Visibility />}
                sx={{ ml: 2 }}
              >
                👁️ พรีวิว PDF
              </Button>
            )}
          </Stack>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
              {error}
            </Alert>
          )}
        </Paper>

        {/* Report Display */}
        {reportData && (
          <>
            {/* Student Info */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Person color="primary" />
                    <Typography variant="h6" fontWeight="bold">ข้อมูลนิสิต</Typography>
                  </Box>
                  <Box sx={{ pl: 4 }}>
                    <Typography><strong>ชื่อ:</strong> {reportData.student_name || "ไม่ระบุชื่อ"}</Typography>
                    <Typography><strong>รหัสนิสิต:</strong> {reportData.student_id || "ไม่ระบุรหัส"}</Typography>
                    <Typography><strong>ระยะเวลาฝึกงาน:</strong> {reportData.internship?.duration ? 
                      `${reportData.internship.duration.start_date} ถึง ${reportData.internship.duration.end_date}` : 
                      "ไม่ระบุ"}</Typography>
                    {reportData.notes && (
                      <Typography sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                        <strong>หมายเหตุ:</strong> {reportData.notes}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Business color="primary" />
                    <Typography variant="h6" fontWeight="bold">ข้อมูลการฝึกงาน</Typography>
                  </Box>
                  <Box sx={{ pl: 4 }}>
                    <Typography><strong>บริษัท:</strong> {reportData.internship?.company || "ไม่ระบุบริษัท"}</Typography>
                    <Typography><strong>ตำแหน่ง:</strong> {reportData.internship?.role || "ไม่ระบุตำแหน่ง"}</Typography>
                    
                    {/* Tech Stack */}
                    {reportData.tech_stack && reportData.tech_stack.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold">เทคโนโลยีที่ใช้:</Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {reportData.tech_stack.map((tech, index) => (
                            <Chip key={index} label={tech} size="small" color="primary" variant="outlined" />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Tasks */}
            {reportData.tasks && reportData.tasks.length > 0 && (
              <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assignment color="primary" />
                    <Typography variant="h6" fontWeight="bold">งานที่ได้รับมอบหมาย ({reportData.tasks.length} งาน)</Typography>
                  </Box>
                  {reportData.tasks.length > 6 && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setShowAllTasks(!showAllTasks)}
                    >
                      {showAllTasks ? '📋 แสดงบางส่วน' : '📋 ดูทั้งหมด'}
                    </Button>
                  )}
                </Box>
                
                <Grid container spacing={2}>
                  {(showAllTasks ? reportData.tasks : reportData.tasks.slice(0, 6)).map((task, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
                        <Typography variant="body2" sx={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          <strong>งานที่ {index + 1}:</strong> {task.summary}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                {!showAllTasks && reportData.tasks.length > 6 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    ... และอีก {reportData.tasks.length - 6} งาน
                  </Typography>
                )}
              </Paper>
            )}

            {/* Skills */}
            {reportData.skills && reportData.skills.length > 0 && (
              <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Code color="primary" />
                  <Typography variant="h6" fontWeight="bold">ทักษะที่ได้รับการพัฒนา</Typography>
                </Box>
                
                <Grid container spacing={3}>
                  {/* Programming Skills */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                      💻 Programming & Technical
                    </Typography>
                    <Stack spacing={1}>
                      {reportData.skills
                        .filter(skill => skill.category === 'programming')
                        .map((skill, index) => (
                          <Chip 
                            key={index} 
                            label={`${skill.name} ${skill.level ? `(${skill.level})` : ''}`}
                            color="primary" 
                            variant="outlined" 
                            size="small"
                          />
                        ))}
                    </Stack>
                  </Grid>

                  {/* Tools */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" fontWeight="bold" color="success" sx={{ mb: 1 }}>
                      🛠️ Tools & Platforms
                    </Typography>
                    <Stack spacing={1}>
                      {reportData.skills
                        .filter(skill => skill.category === 'tools')
                        .map((skill, index) => (
                          <Chip 
                            key={index} 
                            label={`${skill.name} ${skill.level ? `(${skill.level})` : ''}`}
                            color="success" 
                            variant="outlined" 
                            size="small"
                          />
                        ))}
                    </Stack>
                  </Grid>

                  {/* Hardware */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle1" fontWeight="bold" color="warning" sx={{ mb: 1 }}>
                      ⚡ Hardware & Electronics
                    </Typography>
                    <Stack spacing={1}>
                      {reportData.skills
                        .filter(skill => skill.category === 'hardware')
                        .map((skill, index) => (
                          <Chip 
                            key={index} 
                            label={`${skill.name} ${skill.level ? `(${skill.level})` : ''}`}
                            color="warning" 
                            variant="outlined" 
                            size="small"
                          />
                        ))}
                    </Stack>
                  </Grid>
                </Grid>

                {/* Soft Skills */}
                {reportData.soft_skills && reportData.soft_skills.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="info" sx={{ mb: 1 }}>
                      🤝 Soft Skills
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                      {reportData.soft_skills.map((skill, index) => (
                        <Chip 
                          key={index} 
                          label={`${skill.name} ${skill.level ? `(${skill.level})` : ''}`}
                          color="info" 
                          variant="outlined" 
                          size="small"
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Paper>
            )}

            {/* Outcomes */}
            {reportData.outcomes && reportData.outcomes.length > 0 && (
              <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <EmojiEvents color="primary" />
                  <Typography variant="h6" fontWeight="bold">ผลงานที่ส่งมอบ</Typography>
                </Box>
                
                <Grid container spacing={2}>
                  {reportData.outcomes.map((outcome, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Chip 
                            label={outcome.type} 
                            color={
                              outcome.type === 'deliverable' ? 'success' : 
                              outcome.type === 'report' ? 'info' : 
                              outcome.type === 'bugfix' ? 'warning' : 'default'
                            }
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {outcome.detail}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            {/* AI Analysis */}
            {reportData.ai_analysis && (
              <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Psychology color="primary" />
                  <Typography variant="h6" fontWeight="bold">การวิเคราะห์ AI</Typography>
                  {reportData.ai_analysis.performance_score && (
                    <Chip 
                      label={`คะแนน: ${reportData.ai_analysis.performance_score}/100`}
                      color={getScoreColor(reportData.ai_analysis.performance_score)}
                      sx={{ ml: 2 }}
                    />
                  )}
                </Box>
                
                {reportData.ai_analysis.summary && (
                  <Typography variant="body1" sx={{ mb: 3, p: 2, bgcolor: '#f0f8ff', borderRadius: 1 }}>
                    <strong>สรุปผลการประเมิน:</strong> {reportData.ai_analysis.summary}
                  </Typography>
                )}

                <Grid container spacing={3}>
                  {/* Strengths */}
                  {reportData.ai_analysis.strengths && reportData.ai_analysis.strengths.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="h6" sx={{ mb: 2, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmojiEvents /> จุดแข็ง
                        </Typography>
                        <Stack spacing={1}>
                          {reportData.ai_analysis.strengths.map((strength, index) => (
                            <Chip key={index} label={strength} color="success" variant="outlined" />
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                  )}

                  {/* Improvements */}
                  {reportData.ai_analysis.improvements && reportData.ai_analysis.improvements.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="h6" sx={{ mb: 2, color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingUp /> จุดที่ต้องปรับปรุง
                        </Typography>
                        <Stack spacing={1}>
                          {reportData.ai_analysis.improvements.map((improvement, index) => (
                            <Chip key={index} label={improvement} color="warning" variant="outlined" />
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                  )}

                  {/* Recommendations */}
                  {reportData.ai_analysis.recommendations && reportData.ai_analysis.recommendations.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="h6" sx={{ mb: 2, color: 'info.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Notes /> ข้อเสนอแนะ
                        </Typography>
                        <Stack spacing={1}>
                          {reportData.ai_analysis.recommendations.map((recommendation, index) => (
                            <Chip key={index} label={recommendation} color="info" variant="outlined" />
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}
          </>
        )}
      </Paper>

      {/* PDF Preview Modal */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              📄 พรีวิว Skill Mapping Certificate
            </Typography>
            <IconButton onClick={() => setPreviewOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <iframe
              id="pdf-preview-frame"
              title="PDF Preview"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: '600px'
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} variant="outlined">
            ปิด
          </Button>
          {reportData && reportData.tasks && reportData.tasks.length > 10 && (
            <Button
              onClick={() => {
                setShowAllTasks(!showAllTasks);
                // รีเจนเนอเรต PDF ด้วยค่า showAllTasks ใหม่
                setTimeout(() => generateSkillMappingPDF(false), 100);
              }}
              variant="outlined"
              color="primary"
            >
              {showAllTasks ? '📋 แสดงงานบางส่วน' : '📋 ดูงานทั้งหมด'}
            </Button>
          )}
          <Button
            onClick={() => generateSkillMappingPDF(true)}
            variant="contained"
            color="success"
            startIcon={<Download />}
          >
            ดาวน์โหลด PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AIAnalysisReport;